import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getAvailableTiers, type SubscriptionTier } from '../services/subscriptionService';

const PREMIUM_SUBSCRIPTION_COLLECTION = 'premiumSubscriptions';

const timestampToDate = (value: any): Date | null => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (typeof value === 'number') {
    return new Date(value);
  }
  return null;
};

export const useAdFree = () => {
  const { user } = useAuth();
  const [isAdFree, setIsAdFree] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setIsAdFree(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const subscriptionRef = doc(db, PREMIUM_SUBSCRIPTION_COLLECTION, user.uid);
    
    // Use Firebase real-time listener for instant updates
    const unsubscribe = onSnapshot(
      subscriptionRef,
      async (snapshot) => {
        try {
          if (!snapshot.exists()) {
            setIsAdFree(false);
            setIsLoading(false);
            return;
          }

          const data = snapshot.data();
          const tiers = await getAvailableTiers();
          const tier = tiers.find(t => t.id === data.tierId) || tiers.find(t => t.id === 'premiumMonthly');

          if (!tier) {
            setIsAdFree(false);
            setIsLoading(false);
            return;
          }

          const isActive = data.status === 'active' && !data.cancelAtPeriodEnd;
          setIsAdFree(Boolean(isActive && tier.adFree));
        } catch (error) {
          console.error('Error checking ad-free status:', error);
          setIsAdFree(false);
        } finally {
          setIsLoading(false);
        }
      },
      (error) => {
        console.error('Subscription listener error:', error);
        setIsAdFree(false);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  return {
    isAdFree,
    isLoading
  };
};

export default useAdFree;
